import { useEffect, useState } from "react";
import { ServiceTypes } from "src/api/adminMemo/adminmemo-types";
import { useApiOperation } from "src/api/hooks";
import { editIotDeviceNameAsync, getIotManagementControlAsync, getIotManagementControlExecuteAsync } from "src/api/iot/iot-api";
import { BuildingDeviceControl, DeviceControlParams, IoTDeviceRegistration, IotPlatformCategoryLabel } from "src/api/iot/iot-types";
import AdminMemoV3 from "src/components/AdminMemoV3/AdminMemoV3";
import { BaseButton } from "src/components/BaseButton";
import { BaseInput } from "src/components/BaseInput";
import GoToListButton from "src/components/GoToListButton";
import RegistrationInformation from "src/components/RegistrationInformation";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import ControlDeviceTable from "src/pages/building/forms/iotDevice/components/ControlDeviceTable";
import { useModal } from "src/recoil/modalState/hook";
import { UnionPartnerPrivilegeCode } from "src/types/partner";
import { isValidJSONString } from "src/utils/common-util";

type Props = {
  detail: IoTDeviceRegistration;
  fetchIotDeviceList: () => Promise<void>;
};

const BasicInfo = ({ detail, fetchIotDeviceList }: Props) => {
  const { isAuthority } = usePartnerAuthority();
  const [controlList, setControlList] = useState<BuildingDeviceControl[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [primaryDisplayName, setPrimaryDisplayName] = useState(detail?.primaryDisplayName);

  const { setBaseModal } = useModal();

  // 디바이스 제어 목록
  const { executeAsync: getDeviceControl } = useApiOperation(getIotManagementControlAsync);
  // 디바이스 제어 실행
  const { executeAsync: deviceControlExcute } = useApiOperation(getIotManagementControlExecuteAsync);
  // 디바이스 이름 수정
  const { executeAsync: editIotDeviceName } = useApiOperation(editIotDeviceNameAsync);

  useEffect(() => {
    detail?.id && fetchDeviceControlList();
  }, [detail?.id]);

  const onEditIotDeviceName = async () => {
    await editIotDeviceName({ registeredDeviceId: String(detail?.id), primaryDisplayName: primaryDisplayName || "" });
    setIsEdit(false);
    await fetchIotDeviceList();
  };

  const fetchDeviceControlList = async () => {
    const { data } = await getDeviceControl({ id: String(detail?.id) });
    setControlList(data.data.content);
  };

  /** 디바이스 제어 실행*/
  const excuteDevice = async (
    passData: DeviceControlParams, //
  ) => {
    const { data } = await deviceControlExcute({ ...passData, registeredDeviceId: String(detail?.id) });
    const result = data.data.result;
    const isParsed = isValidJSONString(result);
    setControlList((prev) =>
      [...prev].map((item: BuildingDeviceControl & { executed?: string; isParsed?: boolean; arguments?: string | number }) => {
        if (String(item.control.id) === String(passData.controlId)) {
          item = { ...item, executed: result, isParsed, arguments: passData.arguments };
        }
        return item;
      }),
    );
  };

  // iot 제어구분이 "명령"(EXEC_COMMANDS) 인 실행에 대해 수정 권한확인
  // BUILDING/PRODUCT/CONTRACT 의 "w" 권한과 BASIC 의 "w" 권한을 모두 보유해야 실행 가능
  const checkExecuteAuthority = (deviceType: UnionPartnerPrivilegeCode) => {
    const isAuthorityExecute = isAuthority("w", String(deviceType).toUpperCase() as UnionPartnerPrivilegeCode);
    const isBasicWriteAuthority = isAuthority("w", "BASIC");
    return isAuthorityExecute && isBasicWriteAuthority;
  };

  return (
    <>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          <article className="contents-container__wrap-article">
            <div className="flex-center-between">
              <div className="contents-container__sub-title" style={{ width: 1070 }}>
                <h2>기본정보</h2>
              </div>
              <div>
                {isAuthority("w") && (
                  <div className="flex-center">
                    {isEdit && <BaseButton title="취소 " className="size-small color-white mr5" onClick={() => setIsEdit(false)} />}
                    <BaseButton
                      title={isEdit ? "저장" : "수정"}
                      className="size-small"
                      onClick={() =>
                        isEdit
                          ? setBaseModal({
                              isOpen: true, //
                              title: "저장하시겠습니까?",
                              onClick: () => {
                                onEditIotDeviceName();
                                setBaseModal({ isOpen: false });
                              },
                              onClose: () => setBaseModal({ isOpen: false }),
                              btnLeftTitle: "취소",
                              btnRightTitle: "저장",
                            })
                          : setIsEdit(!isEdit)
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">IoT 구분</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span className="font14">{IotPlatformCategoryLabel[detail?.platformDevice.platformAccount.platform || ""]}</span>
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">기기명</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span className="font14">{detail?.internalDisplayName}</span>
                  </div>
                </div>
              </section>
            </div>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">기기명(노출)</p>
                </div>
                <div className="contents-container__grid-contents">
                  {isEdit ? (
                    <div className="minmax400">
                      <BaseInput className="font14 minmax300" value={primaryDisplayName} onChange={(value: string) => setPrimaryDisplayName(value)} />
                    </div>
                  ) : (
                    <div className="minmax400">
                      <span className="font14">{detail?.primaryDisplayName || "-"}</span>
                    </div>
                  )}
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">Device ID</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span className="font14">{detail?.platformDevice.id}</span>
                  </div>
                </div>
              </section>
            </div>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">Name</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span className="font14">{detail?.platformDevice.name}</span>
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">Type</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span className="font14">{detail?.platformDevice.type}</span>
                  </div>
                </div>
              </section>
            </div>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p className="">Labels</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400 pt10">
                  {detail?.labels && detail.labels.length > 0 ? (
                    detail?.labels?.map((label, idx) => (
                      <span key={`${label.value}-${idx}`} className="font14 mb8">{`${label.name} : ${label.value}`}</span>
                    ))
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </div>
            </section>
            <section className="contents-container__grid mt20">
              <div className="contents-container__grid-index">
                <p className="">제어 정보</p>
              </div>
            </section>
            <section className="contents-container__grid">
              <div className="contents-container__grid-contents">
                <ControlDeviceTable
                  controlList={controlList.sort((a, b) => a.control.displayName.localeCompare(b.control.displayName, "ko"))}
                  excuteDevice={excuteDevice}
                  checkExecuteAuthority={checkExecuteAuthority}
                />
              </div>
            </section>
          </article>
          <RegistrationInformation data={detail} />
          <AdminMemoV3 serviceId={Number(detail?.id)} serviceType={ServiceTypes.SERVICE_IOT_PARTNER_DEVICE} />
        </div>
        {/* 버튼영역 */}
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area">
          <GoToListButton />
        </div>
      </div>
    </>
  );
};

export default BasicInfo;
